import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PlanLimitDialog } from "../components/PlanLimitDialog";

const PlanLimitContext = createContext({ trigger: () => {} });

export function usePlanLimit() {
  return useContext(PlanLimitContext);
}

/**
 * Wraps the app and listens for global error responses.
 * - 402 PLAN_LIMIT_REACHED  → renders PlanLimitDialog
 * - 403 PORTAL_PERMISSION_DENIED  → toasts the structured error message (Phase 19.2)
 */
export function PlanLimitProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  // Phase 19.2: throttle repeated 403 toasts (e.g., dashboard fires multiple gated requests in parallel)
  const lastToastRef = useRef({ key: "", ts: 0 });

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      (err) => {
        try {
          const status = err?.response?.status;
          const data = err?.response?.data;
          const d = data?.detail || data;
          if (status === 402 && d && typeof d === "object" && d.code === "PLAN_LIMIT_REACHED") {
            setDetail(d);
            setOpen(true);
          } else if (status === 403 && d && typeof d === "object" && d.code === "PORTAL_PERMISSION_DENIED") {
            const key = d.permission || "permission";
            const now = Date.now();
            // Suppress duplicates for the same permission within 4 seconds
            if (!(lastToastRef.current.key === key && now - lastToastRef.current.ts < 4000)) {
              lastToastRef.current = { key, ts: now };
              const message = d.message || `You do not have the '${key}' permission on this portal account.`;
              toast.error("Access denied", {
                description: message,
                duration: 5000,
              });
            }
          }
        } catch (_) { /* swallow */ }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const trigger = (d) => { setDetail(d); setOpen(true); };

  return (
    <PlanLimitContext.Provider value={{ trigger }}>
      {children}
      <PlanLimitDialog open={open} onOpenChange={setOpen} detail={detail} />
    </PlanLimitContext.Provider>
  );
}
